import {priority} from '@priority-software/priority-proto-api/lib';
import {date} from '@priority-software/priority-utils/lib/date';
import {getDefaultDateFormat} from '../../../../Localization/date';
import {IReport} from '../../types';
import {getLang} from '../../../../Localization';
import {COL_IDS} from './consts';
import texts from '../../texts';
import {getUsername} from '../../../../_shared/GWTBridge/invoke/genPage';
import {IsCompany, IsExpression} from '../../consts';
import {SingleSeriesChartTypesOptions, MultiSeriesChartTypesOptions} from '../../chartConsts';
import {ReportColumn} from '../../types';
import {IChartConfig} from '../../types';

type GetExpRepParams = {
    reports: priority.netitems.nrest.IReportTitle[];
    nodeId: string;
    requiredInputs: string[];
};

export const getReportTypeTitle = (reportType: priority.netitems.nrest.RepType) => {
    switch (reportType) {
        case priority.netitems.nrest.RepType.RepType_Chart:
            return texts.chartType;
        case priority.netitems.nrest.RepType.RepType_DataSet:
            return texts.typeDataSet;
        case priority.netitems.nrest.RepType.RepType_OldReport:
            return texts.typeOldReport;
        case priority.netitems.nrest.RepType.RepType_Report:
            return texts.report;
        case priority.netitems.nrest.RepType.RepType_Widget:
            return texts.widgetType;
        default:
            return '';
    }
};

export const isReportMatch = (query: string, report: IReport): boolean => {
    const str = query.toLowerCase();
    if (report.title?.toLowerCase().includes(str)) return true;
    if (report.userName?.toLowerCase().includes(str)) return true;
    if (report.baseForm?.toLowerCase().includes(str)) return true;
    if (report.createdDate?.toLowerCase().includes(str)) return true;
    if (report.updateTime?.toLowerCase().includes(str)) return true;
    if (getReportTypeTitle(report.reportType).toLowerCase().includes(str)) return true;
    if (
        report.isGeneral
            ? texts.yes?.toLowerCase().includes(str)
            : texts.no?.toLowerCase().includes(str)
    ) {
        return true;
    }

    return !!(report.userName === getUsername() && texts.currentUser?.toLowerCase().includes(str));
};

export interface SortType {
    id: string;
    isDesc: boolean;
    isDefault?: boolean;
}

const verifyMatchingInputs = (
    requiredInputs: string[],
    candidateInputs: priority.netitems.nrest.IReportInput[]
) => {
    if (requiredInputs.includes(IsCompany)) {
        const candidateHasCompanyInput = candidateInputs.some(input => input.IsCompany);
        if (!candidateHasCompanyInput) {
            return false;
        } else {
            const isCompanyIndex = requiredInputs.findIndex(input => input === IsCompany);
            const remaining = requiredInputs.slice();
            remaining.splice(isCompanyIndex, 1);
            return remaining.every(input => candidateInputs.some(i => i.Colname === input));
        }
    }
    return requiredInputs.every(input => candidateInputs.some(i => i.Colname === input));
};

export const getExplanatoryReports = ({reports, nodeId, requiredInputs}: GetExpRepParams) => {
    if (requiredInputs.includes(IsExpression)) {
        return [];
    }
    const optionalExpReports = reports.filter(report => {
        return (
            report.RootNodeId === nodeId &&
            report.ReportType !== priority.netitems.nrest.RepType.RepType_Chart &&
            report.InUse &&
            verifyMatchingInputs(requiredInputs, report.Inputs)
        );
    });
    return optionalExpReports;
};

export const filterAndSort = (
    searchQuery: string,
    sort: SortType,
    reports: IReport[]
): IReport[] => {
    let list = searchQuery ? reports.filter(report => isReportMatch(searchQuery, report)) : reports;
    if (sort.isDefault) {
        const {currentUser, notActive, rest} = list.reduce(
            (acc, value) => {
                if (!value.isActive) {
                    acc.notActive.push(value);
                } else if (value.userName === getUsername()) {
                    acc.currentUser.push(value);
                } else {
                    acc.rest.push(value);
                }

                return acc;
            },
            {notActive: [], currentUser: [], rest: []}
        );

        const sortedCurrentUser = currentUser.sort((a, b) => sortCompare(a, b, sort));
        const sortedRest = rest.sort((a, b) => sortCompare(a, b, sort));
        const sortedNotActive = notActive.sort((a, b) => sortCompare(a, b, sort));
        return [...sortedCurrentUser, ...sortedRest, ...sortedNotActive];
    } else {
        list = sort ? list.slice().sort((a, b) => sortCompare(a, b, sort)) : list;
    }

    return list;
};

const sortCompare = (value1: IReport, value2: IReport, sort: SortType) => {
    const {id, isDesc} = sort;
    if (id === COL_IDS.createdDate || id === COL_IDS.updateTime) {
        return compareDates(value1[id], value2[id], isDesc);
    }

    return !isDesc
        ? (value1[id]?.toString() || '').localeCompare(value2[id]?.toString() || '')
        : (value2[id]?.toString() || '').localeCompare(value1[id]?.toString() || '');
};

const compareDates = (value1, value2, isDesc) => {
    const dateA = value1 ? new Date(convertToMMDDYY(value1)) : '';
    const dateB = value2 ? new Date(convertToMMDDYY(value2)) : '';

    if (dateA?.toString() === 'Invalid Date' || !dateA) return isDesc ? 1 : -1;
    if (dateB?.toString() === 'Invalid Date' || !dateB) return isDesc ? -1 : 1;

    return !isDesc ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
};

const convertToMMDDYY = date => {
    if (getLang() === '3') return date;
    const parts = date.split('/');
    const day = parts[0];
    const month = parts[1];
    parts[0] = month;
    parts[1] = day;
    return parts.join('/');
};

export const getFormatedDate = (value, isDateTime = false) => {
    if (!value) return '';
    const {year = 0, day = 0, month = 0, minutes = 0, hours = 0} = value;
    const monthIndex = month === 0 ? month : month - 1;
    const dateObj = new Date(year, monthIndex, day, hours, minutes, 0);

    const format = isDateTime
        ? getLang() === '3'
            ? 'MM/DD/YYYY hh:mm A'
            : 'DD/MM/YYYY HH:mm'
        : getDefaultDateFormat();

    return date(dateObj, format);
};

const getNodeName = (col: ReportColumn) => {
    if (col.IsCompany) {
        return IsCompany;
    } else if (col.IsExpression) {
        return IsExpression;
    } else {
        return col.NodeId;
    }
};

export const getRequiredInputs = (chartInfo: IChartConfig, columns: ReportColumn[]): string[] => {
    const requiredInputs = new Set<string>();
    const inputs = columns.filter(col => col.Input);
    switch (chartInfo.type) {
        // Multi-Series
        case MultiSeriesChartTypesOptions.msline:
        case MultiSeriesChartTypesOptions.mscolumn2d:
        case MultiSeriesChartTypesOptions.msbar2d:
        case MultiSeriesChartTypesOptions.stackedcolumn2d:
        case MultiSeriesChartTypesOptions.stackedbar2d:
            requiredInputs.add(getNodeName(chartInfo.xAxis));
            requiredInputs.add(getNodeName(chartInfo.series));
            break;
        // Basic Single-Series
        case SingleSeriesChartTypesOptions.column2d:
        case SingleSeriesChartTypesOptions.bar2d:
        case SingleSeriesChartTypesOptions.line:
        case SingleSeriesChartTypesOptions.pie2d:
        case SingleSeriesChartTypesOptions.doughnut2d:
        case SingleSeriesChartTypesOptions.pyramid:
        case SingleSeriesChartTypesOptions.funnel:
            requiredInputs.add(getNodeName(chartInfo.xAxis));
            break;
        // Bubble Chart
        case SingleSeriesChartTypesOptions.bubble:
            const category = chartInfo.bubbleConfig?.category;
            requiredInputs.add(getNodeName(category));
            break;
    }
    // Gauge and KPI are handled here

    inputs.forEach(input => {
        requiredInputs.add(getNodeName(input));
    });

    return Array.from(requiredInputs);
};

export const getShouldExecute = (type: string, hasRequiredInputs: boolean): boolean => {
    const isKPIorGauge =
        type === SingleSeriesChartTypesOptions.kpi ||
        type === SingleSeriesChartTypesOptions.angulargauge;
    if (isKPIorGauge && !hasRequiredInputs) {
        return false;
    }
    return true;
};
